'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Copy, Check } from 'lucide-react';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';
import { formatCurrency } from '@/lib/formatters/australian-formatters';
import { useCurrencyInput } from './currency-input';
import { FormFieldCompact } from './form-field-compact';

/**
 * Which chrome a field wears.
 *
 * `form` is the stacked label-above-boxed-input every inventory form has always
 * rendered, and stays the default so none of the 22 move. `compact` is the
 * attribute treatment's label-column/value-cell row — opted into per host, not
 * per field, so a surface is one or the other rather than a mix.
 */
export type FormFieldVariant = 'form' | 'compact';

interface FormFieldProps {
  label: string;
  value: string | number | undefined;
  mode: 'read' | 'edit';
  type?: 'text' | 'number' | 'date' | 'textarea' | 'select';
  placeholder?: string;
  options?: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  step?: string;
  onChange?: (value: string | number) => void;
  onBlur?: () => void; // Callback when field loses focus (for validation)
  error?: string; // Validation error message
  className?: string;
  clickToCopy?: boolean; // Enable click-to-copy in read mode (default: false)
  currency?: boolean; // Format as currency (default: false)
  required?: boolean; // Show red asterisk for required fields (default: false)
  disabled?: boolean; // Disable input interaction (default: false)
  loading?: boolean; // Show loading state with animation (default: false)
  clearable?: boolean; // Show "Clear selection" option in select dropdowns (default: false)
  /** When 'enrichment', renders value in blue with a NEW tag (read mode only) */
  changeType?: 'enrichment';
  /**
   * Read mode: render the value as wrapping multi-line text (preserves
   * newlines, top-aligns the label). Use for free-text fields like Notes.
   */
  multiline?: boolean;
  /**
   * Read mode: let a single-line value wrap onto additional lines instead
   * of the default single-line ellipsis truncation. Unlike `multiline`,
   * this doesn't preserve newlines — use for a normally-short value that
   * can occasionally run long (e.g. a linked-asset description).
   */
  wrap?: boolean;
  /**
   * Which chrome to wear (LF-6314). Defaults to `form` — the look every
   * inventory form has today — so adding this moved no shipped pixel. See
   * {@link FormFieldVariant}.
   */
  variant?: FormFieldVariant;
}

/**
 * Reusable form field component that handles both read and edit modes
 * Reduces duplication across inventory form types
 */
export function FormField({
  label,
  value,
  mode,
  type = 'text',
  placeholder,
  options = [],
  step,
  onChange,
  onBlur,
  error,
  className,
  clickToCopy = false,
  currency = false,
  required = false,
  disabled = false,
  loading = false,
  clearable = false,
  changeType,
  multiline = false,
  wrap = false,
  variant = 'form'
}: FormFieldProps) {
  // When loading, also disable the input
  const isDisabled = disabled || loading;
  const [copied, setCopied] = useState(false);
  /*
    The currency field's typing behaviour — thousands separators, the raw
    keystrokes held while focused, and LF-6093's `0` handling — lives in
    `./currency-input` so the compact chrome shares it rather than restating it.
  */
  const currencyInput = useCurrencyInput({
    value,
    onChange: onChange as ((value: number) => void) | undefined
  });

  // Format value for display. A stored `0` is a real recorded figure, not an
  // absent one, so it renders as $0.00 rather than "-" (LF-6093) — a nil balance
  // is a routine probate outcome and every asset/liability form reaches this
  // through ValuePairFields.
  const formatValue = (val: string | number | undefined): string => {
    if (val === 0) return currency ? formatCurrency(0) : '0';
    if (!val) return '-';
    if (currency && typeof val === 'number') {
      return formatCurrency(val);
    }
    if (currency && typeof val === 'string' && !isNaN(parseFloat(val))) {
      return formatCurrency(parseFloat(val));
    }
    return String(val);
  };

  // Handle copy action
  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // `0` is copyable — it is a value the user recorded (LF-6093).
    if (value !== 0 && !value) return;

    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success('Copied to clipboard');

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      toast.error('Failed to copy');
      console.error('Copy failed:', err);
    }
  };

  // Read-only mode
  if (mode === 'read') {
    const displayValue = formatValue(value);

    /*
      **A stored `0` is copyable, and the three guards below have to agree with the
      handler above about that** (LF-6093's intent; the guards were missed, found in
      review of LF-6314).

      `handleCopy` already lets `0` through — a nil balance is a routine probate
      outcome and LF-6093 made `$0.00` a recorded value rather than an absence. The
      JSX guards stayed bare truthy checks, which broke it in two ways at once:
      `onClick` resolved to `undefined`, making that handler unreachable for the one
      figure whose copyability was argued for, and `{clickToCopy && value && <Icon/>}`
      short-circuited to `0`, which React renders as the **text "0"** where the copy
      icon belongs. The second is the frontend rules' "safe JSX conditional
      rendering" clause exactly: `{value && <Component/>}` is banned for a
      non-boolean, because `0` renders.

      One named predicate rather than three inline comparisons, so a fourth guard
      cannot be added in the old shape.
    */
    const copyable = clickToCopy && (value === 0 || !!value);

    return (
      <div
        className={cn(
          'flex min-w-0 gap-2',
          multiline || wrap ? 'items-start' : 'items-center'
        )}
      >
        <span className="text-muted-foreground flex-shrink-0 text-sm font-medium">
          {label}:
        </span>
        <div
          className={cn(
            'flex min-w-0 flex-1 gap-2',
            multiline || wrap ? 'items-start' : 'items-center',
            copyable && 'cursor-pointer'
          )}
          onClick={copyable ? handleCopy : undefined}
        >
          <span
            className={cn(
              'text-sm',
              multiline
                ? 'min-w-0 break-words whitespace-break-spaces'
                : wrap
                  ? 'min-w-0 break-words'
                  : 'truncate',
              changeType === 'enrichment'
                ? 'text-info-subtle-foreground font-medium'
                : 'text-muted-foreground'
            )}
          >
            {displayValue}
          </span>
          {changeType === 'enrichment' && (
            <span className="text-9px bg-info-subtle text-info-subtle-foreground inline-flex shrink-0 items-center rounded px-1 py-px font-semibold tracking-wide uppercase">
              new
            </span>
          )}
          {copyable &&
            (copied ? (
              <Check className="text-success-subtle-foreground h-3 w-3 flex-shrink-0" />
            ) : (
              <Copy className="text-muted-foreground h-3 w-3 flex-shrink-0" />
            ))}
        </div>
      </div>
    );
  }

  // Edit mode

  /*
    The compact chrome is edit-only, and read mode above is shared by both
    variants deliberately — a read-only attribute record has no defined
    appearance (LF-6311 gap K, unsolved), so inventing one in a shared production
    component would answer a question LF-6314 exists to ask. See
    `form-field-compact.tsx`.
  */
  if (variant === 'compact') {
    return (
      <FormFieldCompact
        label={label}
        value={value}
        type={type}
        placeholder={placeholder}
        options={options}
        step={step}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        className={className}
        currency={currency}
        required={required}
        disabled={isDisabled}
        clearable={clearable}
        currencyInput={currencyInput}
      />
    );
  }

  const baseInputClasses = tokens.patterns.form.input;
  const labelClasses = tokens.patterns.form.label;

  if (type === 'textarea') {
    return (
      <div className={cn(tokens.patterns.form.spacing, className)}>
        <label className={labelClasses}>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <Textarea
          value={value?.toString() || ''}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={() => onBlur?.()}
          disabled={isDisabled}
          className={cn(
            'min-h-[60px]',
            baseInputClasses,
            error &&
              'border-destructive focus:ring-destructive bg-error-subtle',
            loading && 'bg-muted animate-pulse'
          )}
          placeholder={placeholder}
        />

        {error && (
          <p
            /* The submit's scroll target — the failing FIELD, preferred over
               the card holding it (VALE-1476). */
            data-validation-error-field
            className="text-destructive flex items-center gap-1 text-xs"
          >
            {error}
          </p>
        )}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div className={cn(tokens.patterns.form.spacing, className)}>
        <label className={labelClasses}>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        <Select
          value={value?.toString() || ''}
          onValueChange={(val) => {
            if (val === '__clear__') {
              onChange?.('');
            } else {
              onChange?.(val);
            }
          }}
          disabled={isDisabled}
        >
          <SelectTrigger
            className={cn(
              baseInputClasses,
              'h-8',
              error &&
                'border-destructive focus:ring-destructive bg-error-subtle',
              loading && 'bg-muted animate-pulse'
            )}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
            {clearable && value && (
              <>
                <SelectSeparator />
                <SelectItem value="__clear__" className="text-muted-foreground">
                  Clear selection
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>

        {error && (
          <p
            /* The submit's scroll target — the failing FIELD, preferred over
               the card holding it (VALE-1476). */
            data-validation-error-field
            className="text-destructive flex items-center gap-1 text-xs"
          >
            {error}
          </p>
        )}
      </div>
    );
  }

  // Default text or number input
  const isMoney = currency && type === 'number';

  return (
    <div className={cn(tokens.patterns.form.spacing, className)}>
      <label className={labelClasses}>
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <div className="relative">
        {isMoney && (
          <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2">
            $
          </span>
        )}
        <Input
          type={isMoney ? 'text' : type}
          step={step}
          disabled={isDisabled}
          value={isMoney ? currencyInput.displayValue : value?.toString() || ''}
          onChange={(e) => {
            if (isMoney) {
              currencyInput.handleChange(e.target.value);
              return;
            }
            const val =
              type === 'number'
                ? parseFloat(e.target.value) || 0
                : e.target.value;
            onChange?.(val);
          }}
          onFocus={isMoney ? currencyInput.handleFocus : undefined}
          onBlur={() => {
            if (isMoney) currencyInput.handleBlur();
            onBlur?.();
          }}
          className={cn(
            baseInputClasses,
            isMoney && 'pl-7',
            error &&
              'border-destructive focus:ring-destructive bg-error-subtle',
            loading && 'bg-muted animate-pulse'
          )}
          placeholder={placeholder}
        />
      </div>

      {error && (
        <p
          /* See the sibling arms above (VALE-1476). */
          data-validation-error-field
          className="text-destructive flex items-center gap-1 text-xs"
        >
          {error}
        </p>
      )}
    </div>
  );
}
